import { Component, OnInit, ViewChild, Input, AfterViewInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { KeepingCurrentReportUrls } from '../../../../../api/KeepingCurrentReportUrls';
import { FileDownloadService } from '../../../../../services/FileDownloadService';
import { AgencyListModalComponent } from '../../../agency/AgencyListModalComponent';
import { KcReportService } from '../../../../../services/KcReportService';
import {
    ApprovedEnrollment,
    ApprovedEnrollmentHistory, BudgetBillingReport, CompletionReport,
    ConfirmOrphanReport,
    EnrollmentReport,
    MissedPaymentReport
} from '../../../../../services/api.client';
import { SHORT_DATE_FORMAT } from '../../../../../constants';
import {startOf, endOf, addTime, nowDate, formatDate} from '../../../../../utilities/date-util';
import { mergeMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

@Component({
    selector: '[keepingCurrentReportForm]',
    templateUrl: './KeepingCurrentReportFormComponent.html',
    styleUrls: ['./KeepingCurrentReportFormComponent.scss'],
    standalone: false
})
export class KeepingCurrentReportFormComponent implements OnInit, AfterViewInit {

    form: UntypedFormGroup;

    get startDate(): FormControl {
        return this.form.get('startDate') as FormControl;
    }

    get endDate(): FormControl {
        return this.form.get('endDate') as FormControl;
    }

    get monthYear(): FormControl {
        return this.form.get('monthYear') as FormControl;
    }

    get startMonthYear(): FormControl {
        return this.form.get('startMonthYear') as FormControl;
    }

    get endMonthYear(): FormControl {
        return this.form.get('endMonthYear') as FormControl;
    }

    get statuses(): FormControl {
        return this.form.get('statuses') as FormControl;
    }

    get agencyId(): FormControl {
        return this.form.get('agencyId') as FormControl;
    }

    dateFormat = SHORT_DATE_FORMAT;

    @Input('showDateRange') showDateRange: boolean;
    @Input('showStatuses') showStatuses: boolean;
    @Input('showAgencies') showAgencies: boolean;
    @Input('showMonthSelection') showMonthSelection: boolean;
    @Input('showMonthRangeSelection') showMonthRangeSelection: boolean;

    approvedEnrollmentHistory: ApprovedEnrollmentHistory[];
    _showExportDescription: boolean;

    @Input('showExportDescription')
    set showExportDescription(value: boolean) {
        this._showExportDescription = value;
        if (this._showExportDescription) {
            this._kcReportService.getApprovedEnrollmentHistory()
                .subscribe((approvedEnrollmentHistory) => this.approvedEnrollmentHistory = approvedEnrollmentHistory);
        }
    }

    get showExportDescription(): boolean {
        return this._showExportDescription;
    }

    exportDescription = new FormControl<Date | string>('new');

    @ViewChild(AgencyListModalComponent)
    agencyListModal: AgencyListModalComponent;

    constructor(private _formBuilder: UntypedFormBuilder,
                private _fileDownloadService: FileDownloadService,
                private _kcReportService: KcReportService) {
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        this.form = this._formBuilder.group({
            startDate: new FormControl(startOf(nowDate(), 'year')),
            endDate: new FormControl(endOf(nowDate(), 'day')),
            statuses: new FormControl(''),
            agencyId: new FormControl(''),
            monthYear: new FormControl(formatDate(startOf(nowDate(), 'month'), 'YYYY-MM')),
            startMonthYear: new FormControl(formatDate(startOf(nowDate(), 'month'), 'YYYY-MM')),
            endMonthYear: new FormControl(formatDate(endOf(addTime(nowDate(), 2, 'months'), 'month'), 'YYYY-MM')),
            exportDescription: new FormControl('new'),
        });
    }

    submit(): Observable<any> | void {
        return void 0;
    }

    submitEnrollmentStatusReport(): Observable<EnrollmentReport[]> {
        const model = this.normalizeDateRange(this.form.value);
        return this._kcReportService.buildEnrollmentReport(model.startDate,
            model.endDate,
            model.agencyId,
            model.statuses);
    }

    downloadEnrollmentStatusReport(): string {
        const model = this.normalizeDateRange(this.form.value);
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_ENROLLMENT_REPORT, model);
    }

    submitMissedPaymentsReport(): Observable<MissedPaymentReport[]> {
        const model = this.normalizeDateRange(this.form.value);
        return this._kcReportService.buildMissedPaymentReport(model.startDate, model.endDate, model.agencyId);
    }

    downloadMissedPaymentsReport(): string {
        const model = this.normalizeDateRange(this.form.value);
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_MISSED_PAYMENT_REPORT, model);
    }

    setDownloadTimeStampForApprovalRecordedApplications(): Observable<ApprovedEnrollmentHistory[]> {
        return this._kcReportService.setDownloadTimeStampForApprovalRecordedApplications()
            .pipe(
                mergeMap(() => {
                    this.exportDescription?.setValue(new Date());
                    this.showExportDescription = true;
                    return this.submitApprovedEnrollmentsReport()
                        .pipe(
                            tap(() => this.exportDescription?.setValue(formatDate(startOf(nowDate(), 'day'), 'YYYY-MM-DDTHH:mm:ss')))
                        );
                })
            );
    }

    submitApprovedEnrollmentsReport(): Observable<ApprovedEnrollment[]> {
        return this._kcReportService.buildApprovedEnrollmentReport(this.exportDescription?.value);
    }

    downloadApprovedEnrollmentsReport(): string {
        let exportDate = formatDate(startOf(this.exportDescription?.value, 'day'), 'YYYY-MM-DDTHH:mm:ss');
        if (!exportDate) {
            exportDate = formatDate(startOf(nowDate(), 'day'), 'YYYY-MM-DDTHH:mm:ss');
        }
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_APPROVED_ENROLLMENTS_REPORT, {
            exportDate
        });
    }

    submitConfirmationOrphansReport(): Observable<ConfirmOrphanReport[]> {
        const model = this.normalizeDateRange(this.form.value);
        return this._kcReportService.buildConfirmOrphanReport(model.startDate, model.endDate, model.agencyId);
    }

    downloadConfirmationOrphansReport(): string {
        const model = this.normalizeDateRange(this.form.value);
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_CONFIRM_ORPHAN_REPORT, model);
    }

    submitBudgetBillingReport(): Observable<BudgetBillingReport[]> {
        const model = this.normalizeDateRange(this.form.value);
        return this._kcReportService.buildBudgetBillingReport(model.monthYear, model.agencyId);
    }

    downloadBudgetBillingReport(): string {
        const model = this.normalizeDateRange(this.form.value);
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_BUDGET_BILLING_REPORT, model);
    }

    submitCompletionReport(): Observable<CompletionReport[]> {
        const model = this.normalizeDateRange(this.form.value);
        return this._kcReportService.buildCompletionReport(model.startMonthYear, model.endMonthYear, model.agencyId);
    }

    downloadCompletionReport(): string {
        const model = this.normalizeDateRange(this.form.value);
        return this._fileDownloadService.buildUrl(KeepingCurrentReportUrls.DOWNLOAD_COMPLETION_REPORT, model);
    }

    private normalizeDateRange(model: any): any {
        model.startDate = startOf(model.startDate, 'day');
        model.endDate = endOf(model.endDate, 'day');
        return model;
    }

    openCalendar(event: Event): void {
        const input = event.target as HTMLInputElement;
        setTimeout(() => input.showPicker(), 0);
    }

    ngAfterViewInit(): void {
    }
}
